import { useState, useMemo } from 'react';
import BlogCard from './BlogCard';
import { blogPosts } from '../../../mocks/blogPosts';

interface BlogGridProps {
  selectedCategory: string;
  searchQuery: string;
}

export default function BlogGrid({ selectedCategory, searchQuery }: BlogGridProps) {
  const filteredPosts = useMemo(() => {
    let filtered = blogPosts;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [selectedCategory, searchQuery]);

  const featuredPost = filteredPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  if (filteredPosts.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-gray-100 rounded-full">
          <i className="ri-file-search-line text-3xl text-gray-400"></i>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
        <p className="text-gray-600 mb-6">
          {searchQuery.trim() 
            ? `No articles match your search for "${searchQuery}"`
            : `No articles found in the ${selectedCategory} category`
          }
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 bg-[#0066CC] text-white rounded-lg font-semibold hover:bg-[#0055AA] transition-colors cursor-pointer whitespace-nowrap"
        >
          View All Articles
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Featured Post */}
      {featuredPost && (
        <div>
          <div className="flex items-center mb-6">
            <div className="flex items-center space-x-2">
              <i className="ri-star-fill text-yellow-500 text-lg"></i>
              <h2 className="text-2xl font-bold text-gray-900">Featured Article</h2>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent ml-4"></div>
          </div>
          <BlogCard post={featuredPost} featured={true} />
        </div>
      )}

      {/* Regular Posts Grid */}
      {regularPosts.length > 0 && (
        <div>
          {featuredPost && (
            <div className="flex items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Latest Articles</h2>
              <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent ml-4"></div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}